import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SEASON, SeasonBadge, Tabs } from "@/components/tyres/common";
import { api, errMsg } from "@/lib/api";

const NONE = "__none";
const blankType = { brand: "", model: "", size: "", season: "ESTIVA", vehicle_models: [], notes: "" };
const blankShop = { name: "", phone: "", email: "", address: "", notes: "" };
const Field = ({ label, k, f, setF, ph }) => <div className="space-y-1.5"><Label>{label}</Label><Input value={f[k]} placeholder={ph} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`tyre-set-${k}`} /></div>;

const EditDialog = ({ item, kind, models, onClose, onSaved }) => {
  const [f, setF] = useState(item);
  const url = kind === "type" ? "/tyres/types" : "/tyres/shops";
  const save = async () => {
    try { item.id ? await api.put(`${url}/${item.id}`, f) : await api.post(url, f); toast.success("Salvato"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  const tog = (m) => setF({ ...f, vehicle_models: f.vehicle_models.includes(m) ? f.vehicle_models.filter((x) => x !== m) : [...f.vehicle_models, m] });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="tyre-settings-dialog">
        <DialogHeader><DialogTitle>{kind === "type" ? "Tipologia gomma" : "Gommista"}</DialogTitle></DialogHeader>
        {kind === "type" ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Marca" k="brand" f={f} setF={setF} ph="Es. Michelin" /><Field label="Modello gomma" k="model" f={f} setF={setF} ph="Es. Agilis" />
            <Field label="Misura" k="size" f={f} setF={setF} ph="Es. 225/65 R16C" />
            <div className="space-y-1.5 sm:col-span-2"><Label>Stagione</Label><Tabs value={f.season} onChange={(v) => setF({ ...f, season: v })} items={Object.entries(SEASON).map(([k, v]) => [k, v.label])} prefix="tyre-set-season" /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Modelli di furgone (nessuno = tutti)</Label>
              <div className="flex flex-wrap gap-2">{models.map((m) => (
                <button key={m} type="button" onClick={() => tog(m)} data-testid={`tyre-set-model-${m}`}
                  className={`rounded-full border px-3 py-1 text-xs font-medium ${f.vehicle_models.includes(m) ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600"}`}>{m}</button>))}</div></div>
            <div className="sm:col-span-2"><Field label="Note" k="notes" f={f} setF={setF} /></div>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2"><Field label="Nome" k="name" f={f} setF={setF} /></div>
            <Field label="Telefono" k="phone" f={f} setF={setF} /><Field label="Email" k="email" f={f} setF={setF} />
            <div className="sm:col-span-2"><Field label="Indirizzo" k="address" f={f} setF={setF} /></div>
            <div className="sm:col-span-2"><Field label="Note" k="notes" f={f} setF={setF} /></div>
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={kind === "type" ? !f.brand.trim() || f.size.trim().length < 3 : f.name.trim().length < 2} data-testid="tyre-set-save">Salva</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ children, onEdit, onDelete, id }) => (
  <div className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={id}>
    <div className="min-w-0 flex-1">{children}</div>
    <Button size="sm" variant="ghost" onClick={onEdit} data-testid={`${id}-edit`}><Pencil className="h-4 w-4" /></Button>
    <Button size="sm" variant="ghost" className="text-slate-400" onClick={onDelete} data-testid={`${id}-delete`}><Trash2 className="h-4 w-4" /></Button>
  </div>
);

export const SettingsTab = ({ types, shops, stations, onChanged }) => {
  const [edit, setEdit] = useState(null);
  const [models, setModels] = useState([]);
  useEffect(() => { api.get("/fleet/vehicles").then((r) => setModels([...new Set(r.data.vehicles.map((v) => (v.model || "").toUpperCase()).filter(Boolean))].sort())); }, []);
  const del = async (url, name) => {
    if (!window.confirm(`Eliminare ${name}?`)) return;
    try { await api.delete(url); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const setDefault = async (sid, v) => {
    try { await api.put(`/tyres/defaults/${sid}`, { shop_id: v === NONE ? null : v }); toast.success("Gommista di default salvato"); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid gap-6 xl:grid-cols-2">
      <section>
        <div className="mb-2 flex items-center justify-between"><h3 className="font-semibold">Tipologie gomme</h3><Button size="sm" onClick={() => setEdit({ kind: "type", item: blankType })} data-testid="tyre-type-add"><Plus className="mr-1 h-4 w-4" /> Nuova</Button></div>
        <div className="divide-y rounded-xl border border-slate-200 bg-white">
          {types.map((t) => (
            <Row key={t.id} id={`tyre-type-${t.id}`} onEdit={() => setEdit({ kind: "type", item: t })} onDelete={() => del(`/tyres/types/${t.id}`, t.label)}>
              <p className="font-medium">{t.label.split(" · ")[0]} <SeasonBadge season={t.season} /></p>
              <p className="text-xs text-slate-500">{t.vehicle_models.length ? t.vehicle_models.join(", ") : "Tutti i modelli"}</p>
            </Row>
          ))}
          {!types.length && <p className="p-4 text-center text-sm text-slate-500">Nessuna tipologia: aggiungila per iniziare.</p>}
        </div>
      </section>
      <section>
        <div className="mb-2 flex items-center justify-between"><h3 className="font-semibold">Gommisti</h3><Button size="sm" onClick={() => setEdit({ kind: "shop", item: blankShop })} data-testid="tyre-shop-add"><Plus className="mr-1 h-4 w-4" /> Nuovo</Button></div>
        <div className="mb-5 divide-y rounded-xl border border-slate-200 bg-white">
          {shops.shops.map((s) => (
            <Row key={s.id} id={`tyre-shop-${s.id}`} onEdit={() => setEdit({ kind: "shop", item: s })} onDelete={() => del(`/tyres/shops/${s.id}`, s.name)}>
              <p className="font-medium">{s.name}</p><p className="truncate text-xs text-slate-500">{[s.phone, s.email, s.address].filter(Boolean).join(" · ") || "—"}</p>
            </Row>
          ))}
          {!shops.shops.length && <p className="p-4 text-center text-sm text-slate-500">Nessun gommista.</p>}
        </div>
        <h3 className="mb-2 font-semibold">Gommista di default per stazione</h3>
        <div className="divide-y rounded-xl border border-slate-200 bg-white">
          {stations.map((st) => (
            <div key={st.id} className="flex items-center gap-3 px-3 py-2 text-sm">
              <span className="w-24 shrink-0 font-medium">{st.name}</span>
              <Select value={shops.defaults[st.id] || NONE} onValueChange={(v) => setDefault(st.id, v)}>
                <SelectTrigger data-testid={`tyre-default-${st.id}`}><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value={NONE}>Nessuno</SelectItem>{shops.shops.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          ))}
        </div>
      </section>
      {edit && <EditDialog item={edit.item} kind={edit.kind} models={models} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); onChanged(); }} />}
    </div>
  );
};
